import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import api_endpoint from '../settings';
import './TrackCommentsPanel.css';

const TrackCommentsPanel = ({ trackId, trackTitle, onClose }) => {
  const accessToken = useSelector(state => state.Auth.accessToken);
  const me = useSelector(state => state.API.me);

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchComments();
  }, [trackId]);

  const fetchComments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${api_endpoint}/audio/tracks/${trackId}/comments`);
      const data = await res.json();
      setComments(data.comments || []);
    } catch (e) {
      setError('Could not load comments.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = newComment.trim();
    if (!body || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${api_endpoint}/audio/tracks/${trackId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error('Failed to post comment');
      const comment = await res.json();
      setComments(prev => [comment, ...prev]);
      setNewComment('');
    } catch (e) {
      setError('Could not post comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="comments-panel">
      <div className="comments-panel-header">
        <div className="comments-panel-title">
          <span className="comments-panel-icon">💬</span>
          <div>
            <div className="comments-panel-heading">Comments</div>
            <div className="comments-panel-subheading">{trackTitle}</div>
          </div>
        </div>
        <button className="comments-panel-close" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <div className="comments-panel-body">
        {/* Comment input */}
        {me ? (
          <form className="comment-form" onSubmit={handleSubmit}>
            <textarea
              ref={textareaRef}
              className="comment-input"
              placeholder="Share your thoughts about this stop..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={3}
              maxLength={500}
              disabled={submitting}
            />
            <div className="comment-form-footer">
              <span className="comment-char-count">{newComment.length}/500</span>
              <button
                type="submit"
                className="comment-submit"
                disabled={!newComment.trim() || submitting}
              >
                {submitting ? 'Posting...' : 'Post'}
              </button>
            </div>
          </form>
        ) : (
          <div className="comment-login-prompt">
            <span>Sign in to leave a comment</span>
          </div>
        )}

        {error && <div className="comment-error">{error}</div>}

        {/* Comments list */}
        <div className="comments-list">
          {loading ? (
            <div className="comments-loading">Loading comments...</div>
          ) : comments.length === 0 ? (
            <div className="comments-empty">
              <span>No comments yet.</span>
              {me && <span> Be the first!</span>}
            </div>
          ) : (
            comments.map(comment => (
              <div key={comment.id} className="comment-item">
                <div className="comment-avatar">{comment.username[0].toUpperCase()}</div>
                <div className="comment-content">
                  <div className="comment-meta">
                    <span className="comment-username">{comment.username}</span>
                    <span className="comment-date">{formatDate(comment.createdAt)}</span>
                  </div>
                  <p className="comment-body">{comment.body}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackCommentsPanel;
